import React, { useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { useSelector } from 'react-redux';
import { selectAllPoints } from '../../map/slice/mapSelectors';
import { ReduxPointSearch } from '../../search/ReduxPointSearch';
import { PriceComponentSearch } from '../../search/PriceComponentSearch';
import { priceFormatter } from '../../helpers/formatters';
import { Market, Point, PriceComponent } from '@/types';
import { useViewMode } from '../../hooks/useViewMode';
import { DevelopmentFlag } from '../../helpers/DevelopmentFlag';

export type CreateMarketProps = {
  onCreate: (markets: Market[]) => void;
};

export const CreateMarket: React.FC<CreateMarketProps> = ({ onCreate }) => {
  const allPoints = useSelector(selectAllPoints);

  const [point, setPoint] = React.useState<Point | null>(null);
  const [description, setDescription] = React.useState<string>('');
  const [buySell, setBuySell] = React.useState<Market['buy_sell'] | ''>('');
  const [maxVolume, setMaxVolume] = React.useState<number>(0);
  const [price, setPrice] = React.useState<number>(0);
  const [adder, setAdder] = React.useState<number>(0);
  const [priceComponent, setPriceComponent] =
    React.useState<PriceComponent | null>(null);
  const [priceComponentSearchOpen, setPriceComponentSearchOpen] =
    React.useState<boolean>(false);
  const [priceType, setPriceType] = React.useState<'FIXED' | 'VARIABLE'>(
    'FIXED',
  );
  const [viewMode] = useViewMode();

  const openPriceComponentSearch = () => {
    setPriceComponentSearchOpen(true);
  };

  const closePriceComponentSearch = () => {
    setPriceComponentSearchOpen(false);
  };

  const isFormValid = useMemo(() => {
    return Boolean(
      point &&
        ['BUY', 'SELL', 'BOTH'].includes(buySell) &&
        maxVolume &&
        maxVolume >= 0 &&
        ((priceType === 'FIXED' &&
          price !== null &&
          price !== undefined &&
          !Number.isNaN(price)) ||
          (priceType === 'VARIABLE' &&
            adder !== null &&
            adder !== undefined &&
            !Number.isNaN(adder) &&
            priceComponent)),
    );
  }, [point, buySell, description, maxVolume, price]);

  return (
    <div className="mt-3">
      <h4>New Market</h4>
      <form className="form">
        <div className="row">
          <div className="col-6 col-xs-12">
            <div className="input-group">
              <label htmlFor="location" className="input-group-text">
                Point
              </label>
              <ReduxPointSearch
                value={point?.name ?? ''}
                placeholder=""
                className="form-control"
                handler={({ id: pointId }: any) => {
                  setPoint(allPoints[pointId] ?? null);
                }}
              />
              <select
                value={buySell}
                onChange={(evt) =>
                  setBuySell(evt.target.value as Market['buy_sell'] | '')
                }
                className="form-select"
                style={{
                  maxWidth: '170px',
                }}
              >
                <option value="" disabled>
                  Select Type
                </option>
                <option value="BUY">Buy</option>
                <option value="SELL">Sell</option>
                <option value="BOTH">Both</option>
              </select>
            </div>
            <div className="input-group mt-2">
              <textarea
                value={description}
                onChange={(evt) => setDescription(evt.target.value)}
                className="form-control"
                placeholder="Description"
              />
            </div>
            <div className="input-group mt-2">
              <label
                htmlFor="max-volume-form-input"
                className="input-group-text"
              >
                Volume
              </label>
              <input
                id="max-volume-form-input"
                value={maxVolume}
                onChange={(evt) => setMaxVolume(Number(evt.target.value))}
                type="number"
                className="form-control"
                placeholder="Max Volume"
              />
            </div>
            <div className="mt-2">
              <button
                className="btn w-100 btn-success"
                disabled={isFormValid === false}
                type="button"
                onClick={() => {
                  if (isFormValid === false) return;
                  onCreate([
                    {
                      id: null,
                      point: Number(point?.id),
                      buy_sell: buySell || 'BOTH',
                      description,
                      max_volume: maxVolume,
                      price: priceType === 'FIXED' ? price : adder,
                      base_price: priceType === 'FIXED' ? price : 0,
                      adder: priceType === 'VARIABLE' ? adder : 0,
                      price_component:
                        priceType === 'VARIABLE' && priceComponent?.id
                          ? Number(priceComponent.id)
                          : null,
                      price_source: 'USER_DEFINED',
                      price_explanation: `${priceComponent?.description} ${
                        adder < 0 ? '-' : '+'
                      } ${priceFormatter.format(Math.abs(adder))}`,
                      price_component_description: null,
                      component_key: uuid(),
                    },
                  ]);
                }}
              >
                Create
              </button>
            </div>
          </div>
          <div className="col">
            <h4>Price</h4>
            {viewMode === 'development' && (
              <div className="input-group">
                <label htmlFor="price-type-input" className="input-group-text">
                  <DevelopmentFlag className="mx-2" /> Price Type
                </label>
                <select
                  id="price-type-input"
                  className="form-select"
                  value={priceType}
                  onChange={(evt) =>
                    setPriceType(evt.target.value as 'FIXED' | 'VARIABLE')
                  }
                >
                  <option value="FIXED">Fixed</option>
                  <option value="VARIABLE">Variable</option>
                </select>
              </div>
            )}
            <div className="input-group mt-2">
              {priceType === 'FIXED' && (
                <>
                  <label
                    htmlFor="price-form-input"
                    className="input-group-text"
                  >
                    Price
                  </label>
                  <input
                    id="price-form-input"
                    value={price}
                    onChange={(evt) => setPrice(Number(evt.target.value))}
                    type="number"
                    className="form-control"
                    placeholder="Market Price"
                  />
                </>
              )}
              {priceType === 'VARIABLE' && (
                <>
                  <label
                    htmlFor="adder-form-input"
                    className="input-group-text"
                  >
                    Adder
                  </label>
                  <input
                    id="adder-form-input"
                    value={adder}
                    onChange={(evt) => setAdder(Number(evt.target.value))}
                    type="number"
                    className="form-control"
                    placeholder="Adder"
                  />
                  <label
                    htmlFor="price-component-form-input"
                    className="input-group-text"
                  >
                    Reference Price
                  </label>
                  <input
                    readOnly
                    type="text"
                    value={priceComponent?.description ?? ''}
                    className="form-control"
                    onClick={openPriceComponentSearch}
                  />
                  <PriceComponentSearch
                    isOpen={priceComponentSearchOpen}
                    onClose={closePriceComponentSearch}
                    onSelect={(component) => {
                      setPriceComponent(component);
                      closePriceComponentSearch();
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
